import { Controller } from 'stimulus'

import '~/site/pages/cart/payment/affirm'
import '~/site/pages/cart/payment/stripe'
import '~/site/pages/cart/payment/paypal'
import '~/site/pages/cart/payment/quickpay'
import '~/site/pages/cart/payment/quickpay_link'
import '~/site/pages/cart/payment/spreedly'
import '~/site/pages/cart/payment/manual_payment'
import '~/site/pages/cart/payment/nmi_credit_card'
import '~/site/pages/cart/payment/nmi_ach'
import '~/site/pages/cart/payment/mobilepay'
import '~/site/pages/cart/payment/fanbasis'
import '~/site/behaviors/modals'

import './payment_form_component.scss'
import { vatCheckPending } from '~/site/pages/cart/vat_check_lock'
export default class extends Controller {
  static targets = ['paymentMethodRadiosContainer', 'activePaymentProcessor']

  connect() {
    if (this.hasActivePaymentProcessorTarget) {
      this.activePaymentProcessorTarget.dispatchEvent(
        new Event('input', { bubbles: true })
      )
      this.activePaymentProcessorTarget.dispatchEvent(
        new Event('change', { bubbles: true })
      )

      // Deferred (not dispatched inline): this controller's element is an ancestor of other
      // controllers (e.g. the split-payment switch) that need to be connected to receive this
      // event, and Stimulus connects controllers in document order during the initial
      // page-load scan - an ancestor's connect() runs before its descendants', so dispatching
      // synchronously here could fire before they've registered their listeners. A 0ms
      // setTimeout is enough to clear that same-tick race: it's a macrotask, guaranteed to run
      // only after the current synchronous connection pass (which covers the whole page) has
      // finished. It's not a full guarantee on its own though - each controller's JS is its
      // own dynamically-loaded chunk, so load (and therefore connect()) order isn't otherwise
      // fixed - which is why the split-payment switch also reads its initial state directly
      // from this target's data attributes on its own connect(), instead of solely trusting
      // it'll be listening in time to catch this event live (see switches_component_controller).
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('payment_processor:changed', {
            bubbles: true,
            detail: {
              supportsSplitPayments:
                this.activePaymentProcessorTarget.getAttribute(
                  `data-${this.identifier}-supports-split-payments-param`
                ) === 'true',
            },
          })
        )
      }, 0)
    }
  }

  changePaymentProcessor({
    params: { processorIndex, supportsSplitPayments },
  }) {
    const radio = this.paymentMethodRadiosContainerTargets[
      processorIndex
    ].querySelector("input[type='radio']")
    radio.checked = true

    // ensure we don't show errors from a previous Stripe processor
    const stripeErrors = document.getElementById('stripe_errors')
    if (stripeErrors) stripeErrors.style.display = 'none'

    radio.dispatchEvent(new Event('input', { bubbles: true }))
    radio.dispatchEvent(new Event('change', { bubbles: true }))

    document.dispatchEvent(
      new CustomEvent('payment_processor:changed', {
        bubbles: true,
        detail: { supportsSplitPayments },
      })
    )
  }

  handlePaymentFormSubmit(e) {
    if (document.querySelector('.js-disabled-for-stripe')) {
      e.preventDefault()
      return false
    }

    // A VIES lookup for the entered VAT number is still running. Submitting now would charge
    // the total on screen while the answer that decides it is in flight — reverse charge can
    // take the tax to zero, so paying first means paying VAT the buyer may not owe. This is
    // the real guard: the greyed button and notice are only what the buyer sees, and Enter
    // bypasses those entirely.
    if (vatCheckPending()) {
      e.preventDefault()
      // Each processor also registers its own click handler, delegated on document (see
      // attachDynamicEvent in common/helpers/dom.js). None of them consults defaultPrevented, so
      // preventDefault alone leaves PayPal, QuickPay, MobilePay, Fanbasis, Spreedly and manual
      // payment free to post the checkout anyway. Stopping the event here means they never run.
      // processFormSubmission refuses too, so the lock does not rest on listener ordering.
      e.stopImmediatePropagation()
      return false
    }

    // Populate first, then look at what is still missing — otherwise we'd scroll the buyer to a
    // hidden entrant field this is about to fill in for them.
    this.ensureFirstEntrantIsPopulated(e)

    this.scrollToMissingField(e)

    // A missing required field is what the browser is about to report on its own. Showing the
    // payment-info message on top of that names the wrong problem — the card can be perfectly fine
    // and an unticked consent box be all that stands in the way.
    if (this.hasMissingRequiredField(e)) return

    if (this.newStripeIsNotCompleted()) {
      e.preventDefault()

      let stripeErrorElement = document.getElementById('stripe_errors')
      stripeErrorElement.querySelector('.error-message').innerText =
        this.getSubmitButton().dataset.notCompletedText
      stripeErrorElement.style.display = 'block'
      stripeErrorElement.classList.remove('hidden')

      return false
    }
  }

  // When there is an HTML "required" attribute in a form, the browser should scroll to the first
  // required field that is empty when submitting the form. It often doesn't — reliably so on mobile,
  // but also on desktop for a consent checkbox, whose own box is a small control at the start of a
  // long label. Without this the buyer gets a button that appears to do nothing at all.
  scrollToMissingField(e) {
    const missingField = this.firstMissingRequiredField(e)
    if (!missingField) return

    // The wrapper, not the input: browsers anchor their message on the control, and a consent
    // checkbox's control can sit above the fold while its label is what the buyer needs to read.
    const scrollTarget =
      missingField.closest('.t-field, .checkbox') || missingField

    scrollTarget.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    })
    missingField.focus()
  }

  // Visible only: a required field the buyer cannot even see is one they cannot act on, so the
  // payment-info message is still the most useful thing we have to show them there.
  hasMissingRequiredField(e) {
    const missingField = this.firstMissingRequiredField(e)

    return !!missingField && missingField.offsetParent !== null
  }

  firstMissingRequiredField(e) {
    const form = e.target.closest('form')
    if (!form) return null

    return form.querySelector('input:invalid, select:invalid, textarea:invalid')
  }

  // When we render a new Stripe payment element, where user can enter a new card,
  // we add a class that marks it as not completed, to prevent the form from being submitted.
  // This class will be removed once the user fills in all the required card details.
  // This function returns true if the element exists and is not hidden and visible/focusable.
  // It could exist and still be hidden if the user switched to another payment processor.
  newStripeIsNotCompleted() {
    const stripeElement = document.querySelector(
      '.js-stripe-is-not-completed:not(.hidden)'
    )
    return stripeElement && stripeElement.offsetParent !== null
  }

  getSubmitButton() {
    return this.element.querySelector('button[type="submit"]')
  }

  // Sometimes we hide the fields of the 1st entrant in the order form (e.g. Name, Last Name and Email fields)
  // And we try to use/sync the values of the Purchase Name/Last Name/Email fields. We try to keep them in sync.
  // We've received MANY reports of errors in the order form that suggest that the sync might not be working correctly, leaving the hidden fields empty.
  // With this function we try to detect this scenario and fix it before checking the validity of the form.
  // So with this function we:
  // 1. Check if the 1st entrant fields are hidden
  // 2. Check if any of those fields are empty
  // 3. Check if the source fields are not empty, meaning the sync failed, and so try to populate the hidden field
  ensureFirstEntrantIsPopulated(e) {
    const form = e.target.closest('form')

    const firstNameField = form.querySelector(
      'input[name*="[entrant][1][first_names]"]'
    )

    const lastNameField = form.querySelector(
      'input[name*="[entrant][1][last_name]"]'
    )

    const emailField = form.querySelector('input[name*="[entrant][1][email]"]')

    let lastName

    if (this.shouldPopulate(firstNameField)) {
      const firstNames = document.querySelector(
        'input[name="purchase[first_names]"]'
      )?.value

      if (firstNames) {
        console.log(
          'Populating first entrant first names fields since they are hidden and empty'
        )
        firstNameField.value = firstNames
      } else {
        // It's possible the Purchase do not have a first_names field, but only a name field instead
        // In that case, we still need to have first_names and last_name values for the entrant, so we split the name value
        const name = document.querySelector(
          'input[name="purchase[name]"]'
        )?.value
        if (name) {
          console.log(
            'Purchase has only 1 field for name, but entrant has 2 fields for first_names and last_name. Splitting and populating entrant fields.'
          )
          const parts = name.split(' ')
          firstNameField.value = parts[0]
          lastName = parts.slice(1).join(' ')
        }
      }
    }

    if (this.shouldPopulate(lastNameField)) {
      // we might have already set the last name from the name field
      if (!lastName) {
        lastName = document.querySelector(
          'input[name="purchase[last_name]"]'
        )?.value
      }

      if (lastName) {
        console.log(
          'Populating first entrant last_name field since they are hidden and empty'
        )
        lastNameField.value = lastName
      }
    }

    if (this.shouldPopulate(emailField)) {
      const email = document.querySelector(
        'input[name="purchase[email]"]'
      )?.value
      if (email) {
        console.log(
          'Populating first entrant email field since it is hidden and empty'
        )
        emailField.value = email
      }
    }
  }

  shouldPopulate(field) {
    // Note that we're not using `checkVisibility` here because it's not supported in some runtimes
    // (e.g. Facebook embedded browser)
    return field && $(field).is(':hidden') && !field.value
  }
}
